import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaScrewdriverWrench } from 'react-icons/fa6';
import './ChargerMaintenance.less';

/**
 * "Charger Under Maintenance" view.
 *
 * Shown when the API reports `maintenance_mode = true` for the
 * scanned / opened station, and also as the terminal screen when a
 * race-condition session-start request comes back with
 * `error: 'station_under_maintenance'`.
 *
 * The design deliberately mirrors ChargerNotFound so users who bounce
 * between blocked states see a consistent skeleton — the only things
 * that change are the copy, the icon tint, and the primary action
 * ("Find another charger" here vs. "Scan another charger" there).
 *
 * The charge start controls are never rendered from this screen — the
 * caller in EvProfile returns early so connector selection, payment
 * controls, and Get-Started never mount when maintenance is active.
 */
const ChargerMaintenance = ({ chargerCode, reason, onRetry }) => {
  const navigate = useNavigate();

  const findAnother = () => {
    // Routes to the existing nearby-charger surface. That page already
    // hides start controls behind the maintenance override for other
    // impacted chargers via the shared classifier.
    navigate('/charge');
  };

  const tryAgain = () => {
    if (typeof onRetry === 'function') {
      onRetry();
      return;
    }
    // Same-URL refetch — covers the case where the admin has since
    // returned the charger to service.
    window.location.reload();
  };

  return (
    <div className="ev-content-wrapper charger-maintenance-wrapper d-flex flex-column align-items-center justify-content-center text-center">
      <div className="charger-maintenance-icon" aria-hidden="true">
        <FaScrewdriverWrench size={72} />
      </div>
      <h2 className="charger-maintenance-title">Charger under maintenance</h2>
      <p className="charger-maintenance-message">
        This charger is currently under maintenance. Please use another charger nearby.
      </p>
      {reason && (
        <p className="charger-maintenance-reason" aria-label="Reason from operator">
          {reason}
        </p>
      )}
      {chargerCode && (
        <p className="charger-maintenance-code">
          <span className="charger-maintenance-code-label">Code</span>
          <code>{chargerCode}</code>
        </p>
      )}
      <div className="charger-maintenance-actions">
        <button
          type="button"
          className="charger-maintenance-btn primary"
          onClick={findAnother}
        >
          Find another charger
        </button>
        <button
          type="button"
          className="charger-maintenance-btn"
          onClick={tryAgain}
        >
          Try again
        </button>
      </div>
    </div>
  );
};

export default ChargerMaintenance;
